import { formatMoney, moneyFromCents, parseMoney, type MoneyErrorCode } from '@wodi/domain';
import { useState } from 'react';

import { TextField, type FieldWidth } from '../TextField/TextField.tsx';

/**
 * What the person sees for each amount error. @wodi/domain's MONEY_ERRORS are developer
 * descriptions; the interface owns its wording. Typed against MoneyErrorCode, so a new
 * code can't ship without a message.
 */
export const MONEY_ERROR_MESSAGES: Record<MoneyErrorCode | 'money.negative', string> = {
  'money.empty': 'Enter an amount.',
  'money.not_a_number': 'Enter an amount in shillings, like 1,200 or 1200.50.',
  'money.too_many_decimals': 'An amount has at most two decimal places, for cents.',
  'money.unknown_currency': 'Only Kenyan shillings can be used.',
  'money.negative': 'Enter an amount of zero or more.',
};

export interface MoneyFieldProps {
  /** Always visible, above the field. */
  label: string;
  /** Integer cents, or `null` when empty or not valid yet. Money is never a float. */
  value: bigint | null;
  /** Called as the person types, with cents when the text is a valid amount. */
  onChange: (cents: bigint | null, error: MoneyErrorCode | 'money.negative' | null) => void;
  description?: string;
  /** An error from elsewhere, e.g. the server. Shown instead of the field's own. */
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  /** Negative amounts are refused unless the caller allows them (credits, refunds). */
  allowNegative?: boolean;
  autoFocus?: boolean;
  name?: string;
  /** Width by expected content. Defaults to `sm`, enough for KES 1,000,000.00. */
  width?: FieldWidth;
}

function check(text: string, isRequired: boolean, allowNegative: boolean) {
  if (text.trim() === '') {
    return { value: null, error: isRequired ? ('money.empty' as const) : null };
  }
  const result = parseMoney(text);
  if (!result.ok) return { value: null, error: result.code };
  if (!allowNegative && result.value.cents < 0n)
    return { value: null, error: 'money.negative' as const };
  return { value: result.value.cents, error: null };
}

function display(cents: bigint): string {
  return formatMoney(moneyFromCents(cents), { showCurrency: false });
}

/**
 * An amount in Kenyan shillings (DESIGN.md §9). Accepts what people type at a counter
 * (`1,200`, `KES 1200.50`, `1 200`), reads it with `parseMoney` so no float is ever
 * involved, and on leaving the field shows it grouped (`1,200.50`). The form receives
 * integer cents.
 */
export function MoneyField({
  label,
  value,
  onChange,
  description,
  errorMessage,
  isRequired = false,
  isDisabled,
  allowNegative = false,
  autoFocus,
  name,
  width = 'sm',
}: MoneyFieldProps) {
  const [text, setText] = useState(value === null ? '' : display(value));
  const [reported, setReported] = useState<bigint | null>(value);
  const [showError, setShowError] = useState(false);

  // The form changed the value itself (a reset): show the new amount.
  if (value !== reported) {
    setReported(value);
    setText(value === null ? '' : display(value));
    setShowError(false);
  }

  const { error } = check(text, isRequired, allowNegative);
  const shownError =
    errorMessage ?? (showError && error !== null ? MONEY_ERROR_MESSAGES[error] : undefined);

  return (
    <TextField
      label={label}
      width={width}
      unit="KES"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      onChange={(next) => {
        setText(next);
        const result = check(next, isRequired, allowNegative);
        setReported(result.value);
        onChange(result.value, result.error);
      }}
      onBlur={() => {
        const result = check(text, isRequired, allowNegative);
        if (result.value !== null) setText(display(result.value));
        setShowError(result.error !== null);
      }}
      {...(description === undefined ? {} : { description })}
      {...(shownError === undefined ? {} : { errorMessage: shownError })}
      {...(isRequired ? { isRequired } : {})}
      {...(isDisabled === undefined ? {} : { isDisabled })}
      {...(autoFocus === undefined ? {} : { autoFocus })}
      {...(name === undefined ? {} : { name })}
    />
  );
}
